import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { getImageDimensions, imageToDataUrl, pdfToImage } from '@/lib/pdf/letterhead-utils';
import { FileImage, Loader, Upload, X } from 'lucide-react';

import { detectContentArea } from '@/modules/templates/services/letterhead.service';
import { LetterheadConfig } from '@/schemas/schema';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { ContentAreaEditor } from './ContentAreaEditor';

interface LetterheadUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (config: LetterheadConfig | null) => void;
  existingLetterhead?: LetterheadConfig;
}

export interface ContentArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function LetterheadUploadDialog({
  open,
  onClose,
  onConfirm,
  existingLetterhead,
}: LetterheadUploadDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(
    existingLetterhead?.imageDataUrl || null
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [contentArea, setContentArea] = useState<ContentArea | null>(
    existingLetterhead
      ? {
          x: existingLetterhead.contentArea.x,
          y: existingLetterhead.contentArea.y,
          width: existingLetterhead.contentArea.width,
          height: existingLetterhead.contentArea.height,
        }
      : null
  );
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(
    existingLetterhead
      ? {
          width: existingLetterhead.pageWidth,
          height: existingLetterhead.pageHeight,
        }
      : null
  );
  const [showEditor, setShowEditor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync internal state when dialog opens with a (possibly updated) existingLetterhead.
  // useState initializers only run once on mount, so if existingLetterhead arrives
  // after mount (e.g. async org letterhead fetch), the state would be stale.
  useEffect(() => {
    if (!open) return;
    if (existingLetterhead) {
      setImageDataUrl(existingLetterhead.imageDataUrl);
      setContentArea({
        x: existingLetterhead.contentArea.x,
        y: existingLetterhead.contentArea.y,
        width: existingLetterhead.contentArea.width,
        height: existingLetterhead.contentArea.height,
      });
      setImageDimensions({
        width: existingLetterhead.pageWidth,
        height: existingLetterhead.pageHeight,
      });
      setShowEditor(true);
    } else {
      setImageDataUrl(null);
      setContentArea(null);
      setImageDimensions(null);
      setShowEditor(false);
    }
  }, [open, existingLetterhead]);

  const handleFileSelect = async (selectedFile: File) => {
    setIsProcessing(true);

    try {
      let dataUrl: string;
      let dimensions: { width: number; height: number };

      if (selectedFile.type === 'application/pdf') {
        // Convert PDF to image
        dataUrl = await pdfToImage(selectedFile);
        dimensions = await getImageDimensions(dataUrl);
      } else if (selectedFile.type.startsWith('image/')) {
        // Convert image to data URL
        dataUrl = await imageToDataUrl(selectedFile);
        dimensions = await getImageDimensions(dataUrl);
      } else {
        throw new Error(t('GENERATEREVIEW_LETTERHEAD_ERRORS_UNSUPPORTEDFILE'));
      }

      setImageDataUrl(dataUrl);
      setImageDimensions(dimensions);
      setIsProcessing(false);

      // Auto-detect content area - pass dimensions directly since state update is async
      await handleDetectContentArea(dataUrl, dimensions);
    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : t('GENERATEREVIEW_LETTERHEAD_ERRORS_PROCESSFAILED')
      });
      setIsProcessing(false);
    }
  };

  const handleDetectContentArea = async (
    dataUrl: string,
    dims?: { width: number; height: number }
  ) => {
    setIsDetecting(true);
    const dimensions = dims || imageDimensions;
    try {
      if (!dimensions) throw new Error('No image dimensions');

      const result = await detectContentArea(dataUrl, dimensions.width, dimensions.height);
      if (result.success && result.contentArea) {
        setContentArea(result.contentArea);
        setShowEditor(true);
      }
    } catch (error) {
      console.error('Error detecting content area:', error);
      const fallbackDims = dims || imageDimensions;
      if (fallbackDims) {
        setContentArea({
          x: fallbackDims.width * 0.1,
          y: fallbackDims.height * 0.15,
          width: fallbackDims.width * 0.8,
          height: fallbackDims.height * 0.7,
        });
        setShowEditor(true);
      }
    } finally {
      setIsDetecting(false);
    }
  };

  const handleConfirm = () => {
    if (!imageDataUrl || !contentArea || !imageDimensions) {
      return;
    }

    // Convert image pixels to PDF points (1/72 inch). Detect A4 vs LETTER
    // from the source aspect ratio so the letterhead isn't squashed into
    // the wrong page format — @react-pdf/renderer renders Image at the
    // exact width/height we save here, and aspect mismatch shows as
    // visible stretching.
    const aspect = imageDimensions.width / imageDimensions.height;
    const A4_RATIO = 595 / 842;
    const LETTER_RATIO = 612 / 792;
    const isA4 = Math.abs(aspect - A4_RATIO) < Math.abs(aspect - LETTER_RATIO);
    const PDF_PAGE_WIDTH = isA4 ? 595 : 612;
    const PDF_PAGE_HEIGHT = isA4 ? 842 : 792;
    const scaleX = PDF_PAGE_WIDTH / imageDimensions.width;
    const scaleY = PDF_PAGE_HEIGHT / imageDimensions.height;

    const letterheadConfig: LetterheadConfig = {
      imageDataUrl,
      pageWidth: PDF_PAGE_WIDTH,
      pageHeight: PDF_PAGE_HEIGHT,
      contentArea: {
        x: contentArea.x * scaleX,
        y: contentArea.y * scaleY,
        width: contentArea.width * scaleX,
        height: contentArea.height * scaleY,
      },
    };

    onConfirm(letterheadConfig);
    handleClose();
  };

  const handleClose = () => {
    setImageDataUrl(existingLetterhead?.imageDataUrl || null);
    setContentArea(
      existingLetterhead
        ? {
            x: existingLetterhead.contentArea.x,
            y: existingLetterhead.contentArea.y,
            width: existingLetterhead.contentArea.width,
            height: existingLetterhead.contentArea.height,
          }
        : null
    );
    setImageDimensions(
      existingLetterhead
        ? {
            width: existingLetterhead.pageWidth,
            height: existingLetterhead.pageHeight,
          }
        : null
    );
    setShowEditor(false);
    onClose();
  };

  const handleRemove = () => {
    setImageDataUrl(null);
    setContentArea(null);
    setImageDimensions(null);
    setShowEditor(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onConfirm(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] sm:w-full max-w-2xl! max-h-[90vh] overflow-y-auto rounded-lg z-9999 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg pr-6">
            {t('GENERATEREVIEW_LETTERHEAD_TITLE')}
          </DialogTitle>
          <DialogDescription>{t('GENERATEREVIEW_LETTERHEAD_DESCRIPTION')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!imageDataUrl ? (
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                className="hidden"
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0];
                  if (selectedFile) {
                    handleFileSelect(selectedFile);
                  }
                }}
              />
              <FileImage className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                {t('GENERATEREVIEW_LETTERHEAD_UPLOADHINT')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing || isDetecting}
              >
                {isProcessing || isDetecting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    {t('GENERATEREVIEW_LETTERHEAD_PROCESSING')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {t('GENERATEREVIEW_LETTERHEAD_CHOOSEFILE')}
                  </>
                )}
              </Button>
            </div>
          ) : showEditor && contentArea && imageDimensions ? (
            <ContentAreaEditor
              imageUrl={imageDataUrl}
              imageWidth={imageDimensions.width}
              imageHeight={imageDimensions.height}
              initialContentArea={contentArea}
              onContentAreaChange={(area) => setContentArea(area)}
              onConfirm={handleConfirm}
              onCancel={handleClose}
            />
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <div className="w-full h-auto border border-secondary-200">
                  <img
                    width={imageDimensions?.width}
                    height={imageDimensions?.height}
                    src={imageDataUrl}
                    alt={t('GENERATEREVIEW_LETTERHEAD_PREVIEWALT')}
                  />
                </div>

                {isDetecting && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                    <div className="text-center text-white">
                      <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
                      <p>{t('GENERATEREVIEW_LETTERHEAD_DETECTING')}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleRemove}>
                  <X className="w-4 h-4 mr-2" />
                  {t('GENERATEREVIEW_LETTERHEAD_REMOVE')}
                </Button>
                <Button
                  onClick={() => {
                    if (imageDataUrl && imageDimensions) {
                      handleDetectContentArea(imageDataUrl, imageDimensions);
                    }
                  }}
                  disabled={isDetecting}
                >
                  {isDetecting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      {t('GENERATEREVIEW_LETTERHEAD_DETECTING')}
                    </>
                  ) : (
                    t('GENERATEREVIEW_LETTERHEAD_DETECTBUTTON')
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
